// src/features/MyGymTV/UK/ReviewD6/ReviewD6.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../../layout/Layout';
import Card from 'react-bootstrap/Card';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner, faPlus, faArrowLeft, faThLarge, faList, faEye } from '@fortawesome/free-solid-svg-icons';
import { Button, Alert, Form, ButtonGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';
import Thumbnail from '../../Thumbnail/Thumbnail';
import MediaPreviewModal from '../../MediaPreviewModal/MediaPreviewModal';
import thumbnailService from '../../../../services/thumbnailService'; 
import './review-d6.scss';

const ReviewD6 = () => {
    const navigate = useNavigate();
    const { selectedClubs } = useSelector((state) => state.ukEntities);
    const [content, setContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filenameFilter, setFilenameFilter] = useState('');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [thumbnailsReady, setThumbnailsReady] = useState(false);
    const [thumbnailProgress, setThumbnailProgress] = useState(null);

    // Modal state for preview
    const [previewModal, setPreviewModal] = useState({
        show: false,
        contentID: null,
        filename: null
    });

    useEffect(() => {
        if (selectedClubs.length === 0) {
            setLoading(false);
            return;
        }

        fetchD6Content();
    }, [selectedClubs]);

    // Batch generate thumbnails when content loads
    useEffect(() => {
        const generateThumbnails = async () => {
            if (content.length === 0) return;

            const contentIds = content.map(item => item.ContentID).filter(Boolean);
            if (contentIds.length === 0) return;

            setThumbnailsReady(false);
            setThumbnailProgress({ current: 0, total: contentIds.length });

            try {
                // First, trigger batch generation on server (sync)
                await thumbnailService.batchGenerateThumbnails(contentIds);

                // Then preload into IndexedDB cache
                await thumbnailService.preloadThumbnails(contentIds, (progress) => {
                    setThumbnailProgress(progress);
                });

                setThumbnailsReady(true);
            } catch (err) {
                console.error('Error generating thumbnails:', err);
                // Still mark as ready so individual thumbnails can try to load
                setThumbnailsReady(true);
            } finally {
                setThumbnailProgress(null);
            }
        };

        generateThumbnails();
    }, [content]);

    const fetchD6Content = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            const clubIds = selectedClubs.map(c => c.ClubId);

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/GetD6Content`,
                { clubIds },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const deduplicatedContent = deduplicateContentByOrderId(response.data);
            setContent(deduplicatedContent);

            // No longer auto-fetching media here - lazy load on click

        } catch (err) {
            console.error('Error fetching D6 content:', err);
            setError('Failed to load content');
        } finally {
            setLoading(false);
        }
    };

    const deduplicateContentByOrderId = (contentArray) => {
        const orderMap = new Map();

        contentArray.forEach(item => {
            if (orderMap.has(item.OrderID)) {
                orderMap.get(item.OrderID).venues.push({
                    VenueID: item.VenueID,
                    VenueName: selectedClubs.find(c => c.ClubId === item.VenueID)?.ClubName
                });
            } else {
                orderMap.set(item.OrderID, {
                    ...item,
                    venues: [{
                        VenueID: item.VenueID,
                        VenueName: selectedClubs.find(c => c.ClubId === item.VenueID)?.ClubName
                    }]
                });
            }
        });

        return Array.from(orderMap.values());
    };

    const handlePreviewClick = (contentID, filename) => {
        setPreviewModal({
            show: true,
            contentID,
            filename
        });
    };

    const handleClosePreview = () => {
        setPreviewModal({
            show: false,
            contentID: null,
            filename: null
        });
    };

    const filterContentByFilename = (contentArray) => {
        if (!filenameFilter.trim()) {
            return contentArray;
        }

        return contentArray.filter(item =>
            item.Filename &&
            item.Filename.toLowerCase().includes(filenameFilter.toLowerCase())
        );
    };

    const categorizeContent = () => {
        const today = moment().startOf('day');

        const nowPlaying = [];
        const scheduled = [];
        const expired = [];

        const filteredContent = filterContentByFilename(content);

        filteredContent.forEach(item => {
            const startDate = item.StartDate ? moment(item.StartDate) : null;
            const endDate = item.EndDate ? moment(item.EndDate) : null;

            if (
                item.IsActive &&
                (!startDate || startDate.isSameOrBefore(today)) &&
                (!endDate || endDate.year() >= 2100 || endDate.isAfter(today))
            ) {
                nowPlaying.push(item);
            } else if (item.IsActive && startDate && startDate.isAfter(today)) {
                scheduled.push(item);
            } else {
                expired.push(item);
            }
        });

        return { nowPlaying, scheduled, expired };
    };

    const getStatusLabel = (item, category) => {
        if (category === 'nowPlaying') {
            if (item.EndDate && moment(item.EndDate).year() < 2100) {
                return `Now playing through ${moment(item.EndDate).format('DD/MM/YYYY')}`;
            }
            return 'Now playing ∞';
        } else if (category === 'scheduled') {
            return `Starting ${moment(item.StartDate).format('DD/MM/YYYY')}`;
        }
        return 'Inactive';
    };

    const getStatusClass = (category) => {
        if (category === 'nowPlaying') return 'now-playing-tag';
        if (category === 'scheduled') return 'scheduled-tag';
        return 'expired-tag';
    };

    const renderGridItem = (item, category) => (
        <div
            className="col-lg-3 col-md-4 col-sm-6 col-12 text-center mx-2 m-2 py-2 border rounded-1"
            key={item.OrderID}
        >
            <div className={`${getStatusClass(category)} d-flex justify-content-between align-items-center w-100`}>
                <span>{getStatusLabel(item, category)}</span>
                <FontAwesomeIcon
                    className="ms-2 text-white"
                    icon={faEdit}
                    onClick={() => navigate(`/uk/d6-details/${item.OrderContentID}`)}
                    style={{ cursor: 'pointer' }}
                />
            </div>
            <div className="content-image-wrapper">
                <Thumbnail
                    aspectRatio="portrait"
                    contentId={item.ContentID}
                    onClick={() => handlePreviewClick(item.ContentID, item.Filename)}
                />
                {item.venues.length > 1 && (
                    <div className="venue-count-badge">
                        {item.venues.length}
                    </div>
                )}
            </div>
            {item.Filename && (
                <div
                    className="mt-2 text-muted small"
                    style={{ wordBreak: 'break-word' }}
                >
                    {item.Filename}
                </div>
            )}
        </div>
    );

    const renderListItem = (item, category) => (
        <div
            className="list-view-item d-flex align-items-center border rounded-1 mb-2 p-2"
            key={item.OrderID}
        >
            <div
                className="list-view-preview-icon me-3"
                onClick={() => handlePreviewClick(item.ContentID, item.Filename)}
                style={{ cursor: 'pointer' }}
                title="Click to preview"
            >
                <FontAwesomeIcon
                    className="text-primary"
                    icon={faEye}
                    size="lg"
                />
            </div>
            <div className="flex-grow-1">
                <div className="d-flex align-items-center mb-1">
                    <span className={`${getStatusClass(category)} px-2 py-1 rounded small me-2`}>
                        {getStatusLabel(item, category)}
                    </span>
                    {item.venues.length > 1 && (
                        <span className="badge bg-secondary">
                            {item.venues.length} venues
                        </span>
                    )}
                </div>
                {item.Filename && (
                    <div
className="text-muted small"
style={{ wordBreak: 'break-word' }}
                    >
                        {item.Filename}
                    </div>
                )}
            </div>
            <div className="ms-3">
                <Button
                    onClick={() => navigate(`/uk/d6-details/${item.OrderContentID}`)}
                    size="sm"
                    variant="outline-primary"
                >
                    <FontAwesomeIcon icon={faEdit} />
                </Button>
            </div>
        </div>
    );

    const renderSection = (items, category, title) => {
        if (items.length === 0) return null;

        return (
            <div className="content-section">
                <Card.Header className="text-end">
                    {title} ({items.length})
                </Card.Header>
                <Card.Body>
                    {viewMode === 'grid' ? (
                        <div className="row">
                            {items.map((item) => renderGridItem(item, category))}
                        </div>
                    ) : (
                        <div className="list-view">
                            {items.map((item) => renderListItem(item, category))}
                        </div>
                    )}
                </Card.Body>
            </div>
        );
    };

    const { nowPlaying, scheduled, expired } = categorizeContent();

    if (selectedClubs.length === 0) {
        return (
            <Layout>
                <div className="review-d6-container mt-5">
                    <Button
                        className="mb-3"
                        onClick={() => navigate('/')}
                        size="sm"
                        variant="outline-secondary"
                    >
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faArrowLeft}
                        />
                        Back to Dashboard
                    </Button>
                    <Alert variant="warning">
                        Please select one or more venues from the menu to review content.
                    </Alert>
                </div>
            </Layout>
        );
    }

    if (loading) {
        return (
            <Layout>
                <div className="review-d6-container mt-5 text-center">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        size="3x"
                        spin
                    />
                    <p className="mt-3">Loading content...</p>
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="review-d6-container mt-5">
                    <Button
                        className="mb-3"
                        onClick={() => navigate('/')}
                        size="sm"
                        variant="outline-secondary"
                    >
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faArrowLeft}
                        />
                        Back to Dashboard
                    </Button>
                    <Alert variant="danger">{error}</Alert>
                </div>
            </Layout>
        );
    }

    if (content.length === 0) {
        return (
            <Layout>
                <div className="review-d6-container mt-5">
                    <Button
                        className="mb-3"
                        onClick={() => navigate('/')}
                        size="sm"
                        variant="outline-secondary"
                    >
                        <FontAwesomeIcon
                            className="me-2"
                            icon={faArrowLeft}
                        />
                        Back to Dashboard
                    </Button>
                    <Alert variant="info">
                        No D6 content found for the selected venues.
                    </Alert>
                    <Button onClick={() => navigate('/uk/add-d6')}>
                        Add D6 Content
                    </Button>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="review-d6-container">
                <Button
                    className="mb-3"
                    onClick={() => navigate('/')}
                    size="sm"
                    variant="outline-secondary"
                >
                    <FontAwesomeIcon
                        className="me-2"
                        icon={faArrowLeft}
                    />
                    Back to Dashboard
                </Button>
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h4>Review D6 Content</h4>
                            <small className="text-muted">
                                Viewing content for {selectedClubs.length} selected venue(s)
                            </small>
                        </div>
                        <Button
                            onClick={() => navigate('/uk/add-d6')}
                            variant="primary"
                        >
                            <FontAwesomeIcon
                                className="me-2"
                                icon={faPlus}
                            />
                            Add D6 Content
                        </Button>
                    </div>

                    {content.length > 0 && (
                        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap">
                            <div className="d-flex align-items-center gap-3">
                                <Form.Control
                                    onChange={(e) => setFilenameFilter(e.target.value)}
                                    placeholder="Filter by filename..."
                                    style={{ maxWidth: '400px' }}
                                    type="text"
                                    value={filenameFilter}
                                />
                                {filenameFilter && (
                                    <Button
                                        onClick={() => setFilenameFilter('')}
                                        size="sm"
                                        variant="outline-secondary"
                                    >
                                        Clear
                                    </Button>
                                )}
                            </div>
                            <ButtonGroup>
                                <Button
                                    onClick={() => setViewMode('grid')}
                                    title="Grid view"
                                    variant={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                                >
                                    <FontAwesomeIcon icon={faThLarge} />
                                </Button>
                                <Button
                                    onClick={() => setViewMode('list')}
                                    title="List view"
                                    variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                                >
                                    <FontAwesomeIcon icon={faList} />
                                </Button>
                            </ButtonGroup>
                        </div>
                    )}
                </div>

                <Card className="border-0">
                    {renderSection(nowPlaying, 'nowPlaying', 'Now Playing')}
                    {renderSection(scheduled, 'scheduled', 'Coming Soon')}
                    {renderSection(expired, 'expired', 'Inactive')}

                    {filenameFilter && nowPlaying.length === 0 && scheduled.length === 0 && expired.length === 0 && (
                        <Alert
                            className="m-3"
                            variant="info"
                        >
                            No content found matching "{filenameFilter}"
                        </Alert>
                    )}
                </Card>

                {/* Preview Modal */}
                <MediaPreviewModal
                    contentID={previewModal.contentID}
                    filename={previewModal.filename}
                    onHide={handleClosePreview}
                    show={previewModal.show}
                />

                {/* Thumbnail Loading Progress */}
                {thumbnailProgress && (
                    <div className="thumbnail-batch-progress">
                        <div className="progress-text">
                            Loading thumbnails... {thumbnailProgress.current}/{thumbnailProgress.total}
                        </div>
                        <div className="progress-bar-container">
                            <div
                                className="progress-bar-fill"
                                style={{ width: `${(thumbnailProgress.current / thumbnailProgress.total) * 100}%` }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default ReviewD6;